import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { apiRequest } from "shared/api/apiRequest";
import { getSocietyFormIdContract } from "shared/api/contracts/society/societyId/forms/formId";
import { getSocietyProfileWorkflowInstanceContract } from "shared/api/contracts/society/societyId/profiles/profileId/workflow/workflowId/instance";
import { getSocietyProfileWorkflowInstanceIdContract } from "shared/api/contracts/society/societyId/profiles/profileId/workflow/workflowId/instance/workflowInstanceId";
import { getSocietyWorkflowIdContract } from "shared/api/contracts/society/societyId/workflow/workflowId";
import { useSurveyJSFormDesignOverrideTemplates } from "shared/components/survey-js-form/forms/useSurveyJSFormDesignOverrideTemplates";
import { useSurveyJSOnCompleteFunction } from "shared/components/survey-js-form/forms/useSurveyJSOnCompleteFunction";
import { useSurveyJSOnCompletingFunction } from "shared/components/survey-js-form/forms/useSurveyJSOnCompletingFunction";
import { useSocietySession } from "shared/context/SocietySessionContext";
import { type FormView } from "shared/mappers/database/form/form";
import { type ProfileView } from "shared/mappers/database/profile/profile";
import { type SocietyView } from "shared/mappers/database/society/society";
import { type WorkflowView } from "shared/mappers/database/workflow/workflow";
import { type WorkflowInstanceView } from "shared/mappers/database/workflow/workflow-instance";
import { type WorkflowInstanceStopped } from "shared/workflow/workflow-instance";
import { type WorkflowInstancePayloadValueBase } from "shared/workflow/workflow-instance-payload";
import {
  type CompleteEvent,
  type CompletingEvent,
  type SurveyModel,
} from "survey-core";

export type SurveyJSFormTemplateData = {
  profile: ProfileView | undefined;
  workflowInstance?: WorkflowInstanceView;
};

export type SurveyJSFormValuesUseQueryData = {
  internalForm: FormView;
  internalWorkflow: WorkflowView;
  internalWorkflowInstance?: WorkflowInstanceView;
};

export type FormValuesHookProps = {
  workflowInstanceId?: number;
  formId?: number;
  conferenceId?: string;
  fetchNewestFirst?: boolean;
  workflowId: number;
  // Form Value Params
  setState?: string;
  overrideTitleTemplate?: string;
  overrideCompleteButtonTextTemplate?: string;
  overrideDescriptionTemplate?: string;
  overrideCompletePageHTML?: string;
  /**
   * Hub path to send the member to after submit. Overrides the redirect of
   * the workflow's wait state (sent as invokeParams.userhubRedirect).
   */
  userhubRedirect?: string;
  defaultPayload?: WorkflowInstancePayloadValueBase;
  profile?: ProfileView;
  onCompleteRefreshCallback?: () => Promise<void>;
  onCompleteCallback?: SurveyJSFormValuesOnCompleteCallback;
};
export type SurveyJSFormValuesOnCompleteCallback = (props: {
  survey: SurveyModel;
  options: CompleteEvent;
  workflowInstanceStopped: WorkflowInstanceStopped;
}) => Promise<void>;
export const useSurveyJSFormValues = ({
  workflowInstanceId: providedWorkflowInstanceId,
  formId: providedFormId,
  workflowId: providedWorkflowId,
  conferenceId: providedConferenceId,
  fetchNewestFirst: providedFetchNewestFirst = false,
  overrideTitleTemplate,
  overrideCompleteButtonTextTemplate,
  overrideDescriptionTemplate,
  defaultPayload,
  setState,
  profile,
  onCompleteCallback,
  onCompleteRefreshCallback,
  overrideCompletePageHTML,
  userhubRedirect,
}: FormValuesHookProps): {
  isLoading: boolean;
  workflow?: WorkflowView;
  form?: FormView;
  workflowInstance?: WorkflowInstanceView;
  overrideTitle: string;
  overrideDescription: string;
  overrideCompleteButtonText: string;
  formData: WorkflowInstancePayloadValueBase;
  onComplete: (sender: SurveyModel, options: CompleteEvent) => Promise<void>;
  /*
   * onCompleting is the SurveyJS event called when the user clicks the complete button,
   * but before the survey is actually completed.
   */
  onCompleting: (sender: SurveyModel, options: CompletingEvent) => void;
  SurveyJSAdminOnCompletingModal: () => React.ReactElement;
} => {
  const society = useSocietySession();

  const { data, isLoading } = useQuery({
    queryKey: [
      "form-values",
      JSON.stringify({
        providedWorkflowInstanceId,
        providedFormId,
        providedWorkflowId,
        providedConferenceId,
        providedFetchNewestFirst,
        societyId: society?.societyId,
        profileId: profile?.profileId,
      }),
    ],
    queryFn: async () => {
      const internalWorkflowInstance = await getWorkflowInstanceFromApi({
        providedWorkflowInstanceId,
        providedWorkflowId,
        providedFetchNewestFirst,
        society,
        profile,
      });
      const internalWorkflow = await getWorkflowFromApi({
        providedWorkflowId,
        internalWorkflowInstance,
        society,
      });
      const internalForm = await getFormFromApi({
        providedFormId,
        internalWorkflow,
        internalWorkflowInstance,
        society,
      });
      return {
        internalForm,
        internalWorkflow,
        internalWorkflowInstance,
      };
    },
  });
  const templateData: SurveyJSFormTemplateData = {
    profile,
    workflowInstance: data?.internalWorkflowInstance,
  };

  const formData: WorkflowInstancePayloadValueBase = useMemo(
    () =>
      data?.internalWorkflowInstance?.workflowInstancePayload?.payloadValue ??
      defaultPayload ??
      profile?.profileWorkflowInstance?.workflowInstancePayload?.payloadValue ??
      {},
    [
      data?.internalWorkflowInstance?.workflowInstancePayload?.payloadValue,
      defaultPayload,
      profile?.profileWorkflowInstance?.workflowInstancePayload?.payloadValue,
    ],
  );

  const { onCompleting, SurveyJSAdminOnCompletingModal, consumeAdminOptions } =
    useSurveyJSOnCompletingFunction();

  const onComplete = useSurveyJSOnCompleteFunction({
    data,
    profile,
    setState,
    overrideCompletePageHTML,
    templateData,
    society,
    onCompleteCallback,
    onCompleteRefreshCallback,
    consumeAdminOptions,
    userhubRedirect,
  });
  const { overrideTitle, overrideDescription, overrideCompleteButtonText } =
    useSurveyJSFormDesignOverrideTemplates({
      overrideCompleteButtonTextTemplate,
      overrideDescriptionTemplate,
      overrideTitleTemplate,
      form: data?.internalForm,
      templateData,
    });
  return {
    workflow: data?.internalWorkflow ?? undefined,
    form: data?.internalForm ?? undefined,
    workflowInstance: data?.internalWorkflowInstance ?? undefined,
    overrideTitle,
    overrideDescription,
    overrideCompleteButtonText,
    formData,
    onComplete,
    onCompleting,
    isLoading,
    SurveyJSAdminOnCompletingModal,
  };
};

const getWorkflowInstanceFromApi = async ({
  providedWorkflowInstanceId,
  providedWorkflowId,
  providedFetchNewestFirst,
  society,
  profile,
}: {
  providedWorkflowInstanceId?: number;
  providedWorkflowId: number;
  providedFetchNewestFirst?: boolean;
  society?: SocietyView;
  profile?: ProfileView;
}): Promise<WorkflowInstanceView | undefined> => {
  if (
    providedWorkflowInstanceId !== undefined &&
    society !== undefined &&
    profile !== undefined
  ) {
    const workflowInstanceResponse = await apiRequest({
      contract: getSocietyProfileWorkflowInstanceIdContract,
      inputs: {
        params: {
          societyId: society!.societyId,
          profileId: profile!.profileId,
          workflowId: providedWorkflowId,
          workflowInstanceId: providedWorkflowInstanceId,
        },
      },
    });
    return workflowInstanceResponse;
  }
  if (
    providedFetchNewestFirst &&
    society !== undefined &&
    profile !== undefined
  ) {
    const response = await apiRequest({
      contract: getSocietyProfileWorkflowInstanceContract,
      inputs: {
        params: {
          societyId: society.societyId,
          profileId: profile.profileId,
          workflowId: providedWorkflowId,
        },
      },
      pagination: {
        page: 1,
        pageSize: 1,
        orderByKey: "lastModifiedId",
        orderType: "desc",
      },
    });
    return response?.results?.[0];
  }
  return undefined;
};
const getWorkflowFromApi = async ({
  providedWorkflowId,
  internalWorkflowInstance,
  society,
}: {
  providedWorkflowId: number;
  internalWorkflowInstance?: WorkflowInstanceView;
  society?: SocietyView;
}): Promise<WorkflowView> => {
  if (internalWorkflowInstance?.workflow !== undefined) {
    return internalWorkflowInstance.workflow;
  }
  return await apiRequest({
    contract: getSocietyWorkflowIdContract,
    inputs: {
      params: {
        societyId: society!.societyId,
        workflowId: providedWorkflowId,
      },
    },
  });
};
const getFormFromApi = async ({
  providedFormId,
  internalWorkflow,
  internalWorkflowInstance,
  society,
}: {
  providedFormId?: number;
  internalWorkflow: WorkflowView | null;
  internalWorkflowInstance?: WorkflowInstanceView;
  society: SocietyView;
}): Promise<FormView> => {
  const potentialForms: (FormView | undefined)[] = [
    internalWorkflowInstance?.workflowInstancePayload?.form,
    internalWorkflowInstance?.certificationInstance?.certificationDefinition
      ?.form,
    internalWorkflow?.certificationDefinition?.[0]?.form,
    internalWorkflow?.conference?.[0]?.registrationForm,
    internalWorkflowInstance?.educationInstance?.educationDefinition?.form,
    internalWorkflowInstance?.certificationInstance?.form,
    internalWorkflowInstance?.educationInstance?.form,
    internalWorkflowInstance?.conferenceRegistration?.conference
      ?.registrationForm,
    internalWorkflowInstance?.submissionInstance?.form,
    internalWorkflowInstance?.submissionInstance?.submissionDefinition
      ?.primaryForm,
    internalWorkflow?.submissionDefinition?.[0]?.primaryForm,
  ];
  if (providedFormId === undefined) {
    const foundFormFromInstance = potentialForms.find((form) => {
      return form !== undefined;
    });
    if (foundFormFromInstance !== undefined) {
      if (foundFormFromInstance.formDesign === undefined) {
        return await apiRequest({
          contract: getSocietyFormIdContract,
          inputs: {
            params: {
              societyId: society.societyId,
              formId: foundFormFromInstance.formId!,
            },
          },
        });
      }
      return foundFormFromInstance;
    }
  }
  if (providedFormId !== undefined) {
    const foundFormMatchingProvidedFormId = potentialForms.find((form) => {
      return form?.formId === providedFormId;
    });
    if (foundFormMatchingProvidedFormId !== undefined) {
      if (foundFormMatchingProvidedFormId.formDesign === undefined) {
        return await apiRequest({
          contract: getSocietyFormIdContract,
          inputs: {
            params: {
              societyId: society.societyId,
              formId: providedFormId,
            },
          },
        });
      }
      return foundFormMatchingProvidedFormId;
    }
    return await apiRequest({
      contract: getSocietyFormIdContract,
      inputs: {
        params: {
          societyId: society.societyId,
          formId: providedFormId,
        },
      },
    });
  }
  throw new Error("Could not identify form to use");
};
